import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiX,
  FiMessageSquare,
  FiTag,
  FiClock,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUser,
  FiUserCheck,
  FiEdit2,
  FiTrash2,
  FiBriefcase,
  FiSave,
  FiPlus,
  FiDollarSign,
  FiLayers,
  FiInfo,
  FiArrowRight,
  FiActivity,
} from "react-icons/fi";
import {
  fetchLeadDetail,
  fetchLeadNotes,
  addNoteToLead,
  deleteNote,
  fetchLeadActivities,
  updateLead,
  removeLeadFromBoard,
  addTagToLead,
  removeTagFromLead,
} from "../../../services/crmApi";
import { salesPersonOptionLabel } from "../../../services/salesPersonApi";
import { TbCash } from "react-icons/tb";

const PRIORITY_OPTIONS = ["Low", "Medium", "High"];
const STATUS_OPTIONS = ["Active", "Inactive", "Interested", "Not Interested"];
const LEAD_SOURCE_OPTIONS = [
  "Facebook",
  "WhatsApp",
  "Instagram",
  "LinkedIn",
  "Email",
  "Referral",
  "Direct",
  "Other",
];

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })} at ${date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })}`;
};

const PRIORITY_COLOR = {
  High: "bg-red-100 text-red-700 border-red-200",
  Medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Low: "bg-green-100 text-green-700 border-green-200",
};

// Returns the best display name we can find on a populated user reference, or
// `null` if the entity has nothing usable. Returning null (instead of "Unknown")
// lets callers decide how to fall back — e.g. audit activity entries from the
// backend usually only carry an `actorRole` and no populated actor object, so
// we should display the role rather than a literal "Unknown".
const getActorName = (entity) => {
  if (!entity || typeof entity !== "object") return null;
  if (entity.name) return entity.name;
  const fl = `${entity.firstName || ""} ${entity.lastName || ""}`.trim();
  return fl || entity.email || null;
};

const LeadDetailModal = ({
  lead: initialLead,
  boardId,
  stages = [],
  consultants = [],
  salesPersons = [],
  isAdmin = false,
  onClose,
  onUpdate,
}) => {
  const [lead, setLead] = useState(initialLead);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [submittingNote, setSubmittingNote] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [newTag, setNewTag] = useState("");

  const leadId = initialLead?._id;

  const loadDetail = useCallback(async () => {
    if (!leadId || !boardId) return;
    try {
      const res = await fetchLeadDetail(boardId, leadId);
      if (res?.data) setLead(res.data);
    } catch (err) {
      // initial lead is enough fallback
    }
  }, [boardId, leadId]);

  const loadNotes = useCallback(async () => {
    if (!leadId || !boardId) return;
    try {
      setLoadingNotes(true);
      const res = await fetchLeadNotes(boardId, leadId);
      setNotes(res?.data || []);
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setLoadingNotes(false);
    }
  }, [boardId, leadId]);

  const loadActivities = useCallback(async () => {
    if (!leadId || !boardId) return;
    try {
      setLoadingActivities(true);
      const res = await fetchLeadActivities(boardId, leadId);
      setActivities(res?.data || []);
    } catch (err) {
      // optional
    } finally {
      setLoadingActivities(false);
    }
  }, [boardId, leadId]);

  useEffect(() => {
    loadDetail();
    loadNotes();
    loadActivities();
  }, [loadDetail, loadNotes, loadActivities]);

  useEffect(() => {
    if (lead && isEditing) {
      setEditForm({
        name: lead.name || "",
        email: lead.email || "",
        phone: lead.phone || "",
        category: lead.category || "",
        priority: lead.priority || "Medium",
        status: lead.status || "Active",
        address: lead.address || "",
        expectedDealRevenue: lead.expectedDealRevenue || "",
        leadSource: lead.leadSource || "",
        leadSourceOther: lead.leadSourceOther || "",
        assignedTo: lead.assignedTo?._id || lead.assignedTo || "",
        salesPerson: lead.salesPerson?._id || lead.salesPerson || "",
      });
    }
  }, [lead, isEditing]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      setSubmittingNote(true);
      await addNoteToLead(boardId, lead._id, { content: newNote.trim() });
      toast.success("Note added");
      setNewNote("");
      await loadNotes();
      await loadActivities();
      onUpdate?.();
    } catch (err) {
      // interceptor handles
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(boardId, lead._id, noteId);
      toast.success("Note deleted");
      await loadNotes();
      await loadActivities();
      onUpdate?.();
      setDeletingNoteId(null);
    } catch (err) {
      // interceptor handles
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.name?.trim()) {
      setError("Lead name is required");
      return;
    }
    if (editForm.email?.trim()) {
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(editForm.email.trim())) {
        setError("Please enter a valid email address");
        return;
      }
    }
    if (editForm.phone?.trim()) {
      const phoneRe = /^[+()\-\s\d]{6,20}$/;
      if (!phoneRe.test(editForm.phone.trim())) {
        setError(
          "Phone number can only contain digits, spaces, +, -, and parentheses (6–20 chars)",
        );
        return;
      }
    }
    if (
      editForm.expectedDealRevenue !== "" &&
      editForm.expectedDealRevenue !== null &&
      editForm.expectedDealRevenue !== undefined
    ) {
      const num = Number(editForm.expectedDealRevenue);
      if (Number.isNaN(num) || num < 0) {
        setError("Expected deal revenue must be a non-negative number");
        return;
      }
    }
    try {
      setSavingEdit(true);
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim() || undefined,
        phone: editForm.phone.trim() || undefined,
        category: editForm.category.trim() || undefined,
        priority: editForm.priority,
        status: editForm.status,
        address: editForm.address.trim() || undefined,
        expectedDealRevenue: editForm.expectedDealRevenue || undefined,
        leadSource: editForm.leadSource || undefined,
        leadSourceOther: editForm.leadSourceOther.trim() || undefined,
      };
      // Only Admin may change the assigned consultant or sales person.
      if (isAdmin) {
        payload.assignedTo = editForm.assignedTo || null;
        payload.salesPerson = editForm.salesPerson || null;
      }
      const res = await updateLead(boardId, lead._id, payload);
      if (res?.data) setLead({ ...lead, ...res.data });
      toast.success("Lead updated successfully");
      setIsEditing(false);
      await loadActivities();
      onUpdate?.();
    } catch (err) {
      // interceptor handles
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteLead = async () => {
    try {
      await removeLeadFromBoard(boardId, lead._id);
      toast.success("Lead deleted");
      onUpdate?.();
      onClose?.();
    } catch (err) {
      // interceptor handles
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    const tag = newTag.trim();
    if (!tag) return;
    try {
      const res = await addTagToLead(boardId, lead._id, { tag });
      if (res?.data) setLead({ ...lead, tags: res.data.tags });
      toast.success(`Tag "${tag}" added`);
      setNewTag("");
      await loadActivities();
      onUpdate?.();
    } catch (err) {
      // interceptor handles
    }
  };

  const handleRemoveTag = async (tag) => {
    try {
      const res = await removeTagFromLead(boardId, lead._id, tag);
      if (res?.data) setLead({ ...lead, tags: res.data.tags });
      toast.success(`Tag "${tag}" removed`);
      await loadActivities();
      onUpdate?.();
    } catch (err) {
      // interceptor handles
    }
  };

  const assignedTo =
    lead?.assignedTo && typeof lead.assignedTo === "object"
      ? lead.assignedTo
      : null;
  const salesPerson =
    lead?.salesPerson && typeof lead.salesPerson === "object"
      ? lead.salesPerson
      : null;

  const stageObj =
    (lead?.column && typeof lead.column === "object" ? lead.column : null) ||
    stages.find(
      (s) =>
        s._id === (lead?.column?._id || lead?.column || lead?.columnId),
    );

  const renderActivity = (activity) => {
    const actor = activity.createdBy || activity.actor || activity.user;
    const actorRole =
      activity.createdByRole || activity.actorRole || "";
    // For audit entries the backend often only sends `actorRole`, with no
    // populated actor reference. Fall back to the role (or a friendly default)
    // so we never display the literal word "Unknown".
    const actorName =
      getActorName(actor) || actorRole || "System";

    if (activity.activityKind === "audit") {
      const meta = activity.meta || {};
      let icon = <FiActivity size={14} className="text-purple-600" />;
      let title = activity.message || "Activity";

      if (activity.type === "stage_change") {
        icon = <FiArrowRight size={14} className="text-blue-600" />;
        title = `Stage changed: ${meta.fromName || "?"} → ${
          meta.toName || "?"
        }`;
      } else if (activity.type === "tag_added") {
        icon = <FiTag size={14} className="text-green-600" />;
        title = `Tag added: ${meta.tag || activity.message}`;
      } else if (activity.type === "tag_removed") {
        icon = <FiTag size={14} className="text-red-600" />;
        title = `Tag removed: ${meta.tag || activity.message}`;
      }

      // Only append the role if we actually have a separate actor name —
      // otherwise actorName is already the role and we'd render "Admin • Admin".
      const showRoleSuffix = Boolean(
        actorRole && actorName && actorName !== actorRole,
      );

      return (
        <div
          key={activity._id}
          className="flex gap-3 pb-3 border-b border-gray-100 last:border-0"
        >
          <div className="w-8 h-8 rounded-full bg-purple-50 border border-purple-100 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800">{title}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {actorName}
              {showRoleSuffix ? ` • ${actorRole}` : ""} •{" "}
              {formatDateTime(activity.createdAt)}
            </p>
          </div>
        </div>
      );
    }

    const showRoleSuffix = Boolean(
      actorRole && actorName && actorName !== actorRole,
    );

    // comment
    return (
      <div
        key={activity._id}
        className="flex gap-3 pb-3 border-b border-gray-100 last:border-0"
      >
        <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
          <FiMessageSquare size={14} className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-800 whitespace-pre-wrap">
            {activity.content || activity.message || ""}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {actorName}
            {showRoleSuffix ? ` • ${actorRole}` : ""} •{" "}
            {formatDateTime(activity.createdAt)}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[92vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <input
                type="text"
                value={editForm.name || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                className="w-full text-2xl font-semibold text-gray-800 border-b border-gray-300 focus:outline-none focus:border-blue-500 pb-1"
              />
            ) : (
              <h2 className="text-2xl font-semibold text-gray-800 truncate">
                {lead?.name || "Lead"}
              </h2>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
              {lead?.priority && (
                <span
                  className={`px-2 py-0.5 rounded border font-medium ${
                    PRIORITY_COLOR[lead.priority] ||
                    "bg-gray-100 text-gray-700 border-gray-200"
                  }`}
                >
                  {lead.priority} Priority
                </span>
              )}
              {lead?.status && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded border border-gray-200">
                  {lead.status}
                </span>
              )}
              {lead?.category && (
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200">
                  {lead.category}
                </span>
              )}
              {stageObj?.columnName && (
                <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded border border-purple-200 flex items-center gap-1">
                  <FiLayers size={10} /> {stageObj.columnName}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center gap-1 disabled:opacity-50"
                >
                  <FiSave size={14} />
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                {activeTab === "details" && (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="p-2 hover:bg-gray-100 text-gray-600 rounded transition-colors"
                      title="Edit lead"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      className="p-2 hover:bg-red-50 text-red-600 rounded transition-colors"
                      title="Delete lead"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </>
                )}
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab("details")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "details"
                ? "text-blue-600 border-blue-600"
                : "text-gray-600 border-transparent hover:text-gray-800"
            }`}
          >
            Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("notes")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "notes"
                ? "text-blue-600 border-blue-600"
                : "text-gray-600 border-transparent hover:text-gray-800"
            }`}
          >
            <FiMessageSquare size={14} />
            Notes ({notes.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("activity")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "activity"
                ? "text-blue-600 border-blue-600"
                : "text-gray-600 border-transparent hover:text-gray-800"
            }`}
          >
            <FiClock size={14} />
            Activity
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm flex justify-between items-center mb-4">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="underline text-xs"
              >
                Dismiss
              </button>
            </div>
          )}

          {activeTab === "details" && (
            <div className="space-y-5">
              {/* Edit form fields when editing */}
              {isEditing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 text-sm">
                    Edit Lead Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editForm.email || ""}
                        onChange={(e) =>
                          setEditForm({ ...editForm, email: e.target.value })
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Phone
                      </label>
                      <input
                        type="text"
                        value={editForm.phone || ""}
                        onChange={(e) =>
                          setEditForm({ ...editForm, phone: e.target.value })
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Category
                      </label>
                      <input
                        type="text"
                        value={editForm.category || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            category: e.target.value,
                          })
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Priority
                      </label>
                      <select
                        value={editForm.priority || "Medium"}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            priority: e.target.value,
                          })
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                      >
                        {PRIORITY_OPTIONS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Status
                      </label>
                      <select
                        value={editForm.status || "Active"}
                        onChange={(e) =>
                          setEditForm({ ...editForm, status: e.target.value })
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    {isAdmin && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Assigned Consultant
                        </label>
                        <select
                          value={editForm.assignedTo || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              assignedTo: e.target.value,
                            })
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                        >
                          <option value="">Unassigned</option>
                          {consultants.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name ||
                                `${c.firstName || ""} ${c.lastName || ""}`.trim() ||
                                c.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {isAdmin && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Sales Person
                        </label>
                        <select
                          value={editForm.salesPerson || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              salesPerson: e.target.value,
                            })
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                        >
                          <option value="">No owner</option>
                          {salesPersons.map((sp) => (
                            <option key={sp._id} value={sp._id}>
                              {salesPersonOptionLabel(sp)}
                            </option>
                          ))}
                        </select>
                        <p className="text-[11px] text-gray-500 mt-1">
                          Only Admin can change a lead's sales person.
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        value={editForm.address || ""}
                        onChange={(e) =>
                          setEditForm({ ...editForm, address: e.target.value })
                        }
                        placeholder="City, Country, Postal Code"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Expected Deal Revenue
                      </label>
                      <input
                        type="number"
                        value={editForm.expectedDealRevenue || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            expectedDealRevenue: e.target.value,
                          })
                        }
                        placeholder="Amount in PKR"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Lead Source
                      </label>
                      <select
                        value={editForm.leadSource || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            leadSource: e.target.value,
                          })
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 bg-white"
                      >
                        <option value="">Select source...</option>
                        {LEAD_SOURCE_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    {editForm.leadSource === "Other" && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Specify Source
                        </label>
                        <input
                          type="text"
                          value={editForm.leadSourceOther || ""}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              leadSourceOther: e.target.value,
                            })
                          }
                          placeholder="Enter other source..."
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-3">
                    <FiUser
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-600">Lead Name</p>
                      <p className="text-sm text-gray-800 font-medium break-all">
                        {lead?.name || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FiMail
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-600">Email</p>
                      {lead?.email ? (
                        <a
                          href={`mailto:${lead.email}`}
                          className="text-sm text-blue-600 hover:underline break-all"
                        >
                          {lead.email}
                        </a>
                      ) : (
                        <p className="text-sm text-gray-400">Not provided</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FiPhone
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Phone</p>
                      {lead?.phone ? (
                        <a
                          href={`tel:${lead.phone}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {lead.phone}
                        </a>
                      ) : (
                        <p className="text-sm text-gray-400">Not provided</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FiMapPin
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Address</p>
                      <p className="text-sm text-gray-800">
                        {lead?.address || (
                          <span className="text-gray-400">Not provided</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lead Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                  Lead Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-3">
                    <FiInfo
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Status</p>
                      <p className="text-sm text-gray-800">
                        {lead?.status || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FiInfo
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Priority</p>
                      <p className="text-sm text-gray-800">
                        {lead?.priority || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FiInfo
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Category</p>
                      <p className="text-sm text-gray-800">
                        {lead?.category || (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FiLayers
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Stage</p>
                      <p className="text-sm text-gray-800">
                        {stageObj?.columnName || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FiInfo
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Lead Source</p>
                      <p className="text-sm text-gray-800">
                        {lead?.leadSource === "Other"
                          ? lead?.leadSourceOther || "Other"
                          : lead?.leadSource || (
                              <span className="text-gray-400">Not set</span>
                            )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    {<TbCash
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    /> }
                    <div>
                      <p className="text-xs text-gray-600">
                        Expected Deal Revenue
                      </p>
                      <p className="text-sm text-gray-800 font-semibold">
                        {lead?.expectedDealRevenue
                          ? `PKR ${Number(
                              lead.expectedDealRevenue,
                            ).toLocaleString()}`
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ownership */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                  Ownership
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start gap-3">
                    <FiUserCheck
                      className="text-indigo-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">Sales Person</p>
                      {salesPerson ? (
                        <>
                          <p className="text-sm text-gray-800 font-medium">
                            {salesPerson.name || ""}
                          </p>
                          {salesPerson.email && (
                            <p className="text-xs text-gray-500">
                              {salesPerson.email}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-400">
                          No owner assigned
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Assigned consultant is the consultant who works/manages
                      the lead — a separate concept from the salesPerson owner.
                      Shown to everyone (SalesPerson, CRM consultant, Admin). */}
                  <div className="flex items-start gap-3">
                    <FiBriefcase
                      className="text-blue-600 mt-1 flex-shrink-0"
                      size={16}
                    />
                    <div>
                      <p className="text-xs text-gray-600">
                        Assigned Consultant
                      </p>
                      {assignedTo ? (
                        <>
                          <p className="text-sm text-gray-800 font-medium">
                            {assignedTo.name ||
                              `${assignedTo.firstName || ""} ${
                                assignedTo.lastName || ""
                              }`.trim() ||
                              assignedTo.email}
                          </p>
                          {assignedTo.email && (
                            <p className="text-xs text-gray-500">
                              {assignedTo.email}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-400">Unassigned</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Array.isArray(lead?.tags) && lead.tags.length > 0 ? (
                    lead.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs"
                      >
                        <FiTag size={12} />
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-blue-900 ml-1"
                          title="Remove tag"
                        >
                          <FiX size={12} />
                        </button>
                      </span>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400">No tags yet</p>
                  )}
                </div>
                <form onSubmit={handleAddTag} className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag..."
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600"
                  />
                  <button
                    type="submit"
                    disabled={!newTag.trim()}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded flex items-center gap-1 disabled:opacity-50"
                  >
                    <FiPlus size={14} />
                    Add
                  </button>
                </form>
              </div>

              {/* Metadata */}
              <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                {lead?.createdAt && (
                  <span>Created {formatDateTime(lead.createdAt)}</span>
                )}
                {lead?.updatedAt && (
                  <span>Updated {formatDateTime(lead.updatedAt)}</span>
                )}
                {/* {lead?.order !== undefined && (
                  <span>Position: {lead.order}</span>
                )} */}
              </div>
            </div>
          )}

          {activeTab === "notes" && (
            <div className="space-y-4">
              <form onSubmit={handleAddNote} className="space-y-2">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note about this lead..."
                  rows={3}
                  maxLength={1000}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 resize-none"
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">
                    {newNote.length}/1000
                  </span>
                  <button
                    type="submit"
                    disabled={!newNote.trim() || submittingNote}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                  >
                    {submittingNote ? "Saving..." : "Add Note"}
                  </button>
                </div>
              </form>

              <div className="space-y-2 mt-4">
                {loadingNotes ? (
                  <p className="text-center text-gray-400 py-4 text-sm">
                    Loading notes...
                  </p>
                ) : notes.length === 0 ? (
                  <p className="text-center text-gray-400 py-6 text-sm">
                    No notes yet. Add the first one above.
                  </p>
                ) : (
                  [...notes].reverse().map((note) => (
                    <div
                      key={note._id}
                      className="bg-gray-50 rounded-md p-3 border border-gray-200"
                    >
                      <div className="flex justify-between items-start mb-1.5">
                        <p className="font-medium text-gray-800 text-xs">
                          {getActorName(note.createdBy) ||
                            note.createdByRole ||
                            "Unknown"}
                          {note.createdByRole && (
                            <span className="ml-1 text-[10px] text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                              {note.createdByRole}
                            </span>
                          )}
                          <span className="text-gray-400 ml-2 font-normal">
                            {formatDateTime(note.createdAt)}
                          </span>
                        </p>
                        <button
                          type="button"
                          onClick={() => setDeletingNoteId(note._id)}
                          className="text-gray-400 hover:text-red-600 p-1"
                          title="Delete note"
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">
                        {note.content}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === "activity" && (
            <div className="space-y-2">
              {loadingActivities ? (
                <p className="text-center text-gray-400 py-4 text-sm">
                  Loading activity...
                </p>
              ) : activities.length === 0 ? (
                <p className="text-center text-gray-400 py-6 text-sm">
                  No activity yet
                </p>
              ) : (
                activities.map((activity) => renderActivity(activity))
              )}
            </div>
          )}
        </div>

        {/* Delete confirm - Lead */}
        {confirmDelete && !deletingNoteId && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center rounded-lg p-4">
            <div className="bg-white rounded-lg p-5 max-w-sm w-full shadow-xl">
              <h3 className="font-semibold text-gray-800 mb-2">
                Delete this lead?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                This will permanently remove the lead and all its notes.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteLead}
                  className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm - Note */}
        {deletingNoteId && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center rounded-lg p-4">
            <div className="bg-white rounded-lg p-5 max-w-sm w-full shadow-xl">
              <h3 className="font-semibold text-gray-800 mb-2">
                Delete this note?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                This will permanently remove the note. This action cannot be
                undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeletingNoteId(null)}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteNote(deletingNoteId)}
                  className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadDetailModal;
